/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */

import { type FC, useState } from 'react';

import { I18n } from '@adp-arch/i18n';
import { Button, Form } from '@coze-arch/coze-design';

export const ForcePasswordChange: FC<{
  changePassword: (newPassword: string) => void;
  loading: boolean;
  error: string;
}> = ({ changePassword, loading, error }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const submit = () => {
    if (newPassword !== confirmPassword) {
      setValidationError(I18n.t('password_confirm_mismatch'));
      return;
    }
    setValidationError('');
    changePassword(newPassword);
  };

  return (
    <>
      <div className="text-[20px] font-medium coze-fg-primary text-center">
        {I18n.t('password_force_change_title')}
      </div>
      <div className="mt-[8px] mb-[20px] text-sm leading-5 coze-fg-secondary text-center">
        {I18n.t('password_force_change_description')}
      </div>
      <Form>
        <Form.Input
          data-testid="password-change.input.new-password"
          noLabel
          field="newPassword"
          type="password"
          onChange={setNewPassword}
          placeholder={I18n.t('password_new_label')}
        />
        <Form.Input
          data-testid="password-change.input.confirm-password"
          noLabel
          field="confirmPassword"
          type="password"
          onChange={setConfirmPassword}
          placeholder={I18n.t('password_confirm_label')}
        />
      </Form>
      <div className="mb-[8px] text-xs coze-fg-secondary">
        {I18n.t('password_policy_hint')}
      </div>
      <Button
        data-testid="password-change.button.submit"
        className="mt-[4px]"
        disabled={!newPassword || !confirmPassword}
        onClick={submit}
        loading={loading}
        color="hgltplus"
      >
        {I18n.t('password_force_change_submit')}
      </Button>
      {validationError || error ? (
        <div
          role="alert"
          className="mt-[12px] rounded-[6px] border border-red-300 bg-red-50 px-[12px] py-[10px] text-sm leading-5 text-red-700"
        >
          {validationError || error}
        </div>
      ) : null}
    </>
  );
};
